"use client";

import { useEffect, useRef } from "react";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { useAuth } from "@/context/ContextProvider";
import { showToast } from "@/app/helpers/showToast";
import { reportUploadIncidents } from "@/app/helpers/uploadIncidentTracking";
import {
  hasUnsafeServiceHistoryUploads,
  initializeServiceHistoryUploadRegistry,
  useServiceHistoryUploadRegistryStatus,
  useServiceHistoryUploadSnapshot,
} from "@/app/[id]/profil/service-historie/fotos/serviceHistoryUploadRegistry";

export default function ServiceHistoryUploadQueueManager() {
  const apiBaseUrl = useBackendUrl();
  const { userData, loading, authResolved } = useAuth();
  const uploads = useServiceHistoryUploadSnapshot();
  const registryStatus = useServiceHistoryUploadRegistryStatus();
  const hasUnsafeUpload = hasUnsafeServiceHistoryUploads(uploads);
  const notifiedFailures = useRef(new Set());

  useEffect(() => {
    if (loading || !authResolved) return;
    void initializeServiceHistoryUploadRegistry({
      apiBaseUrl,
      ownerEmail: userData?.email || null,
    });
  }, [apiBaseUrl, authResolved, loading, userData?.email]);

  useEffect(() => {
    if (loading || !authResolved) return;
    reportUploadIncidents({
      apiBaseUrl,
      uploads,
      registryStatus,
      ownerEmail: userData?.email || null,
      flow: "service_history",
    });
  }, [
    apiBaseUrl,
    authResolved,
    loading,
    registryStatus,
    uploads,
    userData?.email,
  ]);

  useEffect(() => {
    if (
      loading ||
      !authResolved ||
      registryStatus.persistenceAvailable
    ) {
      return undefined;
    }
    const retry = setInterval(() => {
      void initializeServiceHistoryUploadRegistry({
        apiBaseUrl,
        ownerEmail: userData?.email || null,
      });
    }, 10_000);
    return () => clearInterval(retry);
  }, [
    apiBaseUrl,
    authResolved,
    loading,
    registryStatus.persistenceAvailable,
    userData?.email,
  ]);

  useEffect(() => {
    if (!hasUnsafeUpload) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [hasUnsafeUpload]);

  useEffect(() => {
    Object.values(uploads).forEach((entry) => {
      if (entry?.state !== "failed") return;
      const notificationKey = `${entry.key}:${entry.uploadId}`;
      if (notifiedFailures.current.has(notificationKey)) return;
      notifiedFailures.current.add(notificationKey);
      showToast(
        `„${entry.fileName}“ konnte nicht hochgeladen werden. Bitte entferne die Datei und wähle sie erneut aus.`,
        "error",
      );
    });
  }, [uploads]);

  return null;
}
